import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PublishIcon from "@material-ui/icons/Publish";
import PeopleIcon from "@material-ui/icons/People";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getTokens } from "../../styles/tokens";
// import { SocketContext } from "../../context/Socket/SocketContext";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    search: {
      width: "min(320px, 100%)",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: { minWidth: 720 },
    nameCell: { fontWeight: 600, color: t.text },
    muted: { color: t.textMuted },
    actionsCell: { width: 110, whiteSpace: "nowrap" },
    deleteButton: { color: t.semantic.error },
    emptyActions: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: 8,
    },
  };
});

const ContactListItems = () => {
  const classes = useStyles();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    }
    socket.on(`company-${companyId}-ContactListItem`, onCompanyContactLists);

    return () => {
      socket.off(`company-${companyId}-ContactListItem`, onCompanyContactLists);
    };
  }, [contactListId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    history.push("/contact-lists");
  };

  const handlePickImportFile = () => {
    fileUploadRef.current.value = null;
    fileUploadRef.current.click();
  };

  return (
    <MainContainer maxWidth>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name
            }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={planilhaExemplo} download="planilha.xlsx">
              Clique aqui para baixar planilha exemplo.
            </a>
          </>
        )}
      </ConfirmationModal>
      {
        user.profile === "user" && user?.showCampaign === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <PageHeader
              title={contactList?.name || i18n.t("contactListItems.title")}
              subtitle="Contatos que recebem as campanhas enviadas para esta lista."
              badge={
                <StatusBadge
                  color="neutral"
                  dot={false}
                  label={String(contacts.length)}
                />
              }
              actions={
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={goToContactLists}
                  >
                    {i18n.t("contactListItems.buttons.lists")}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PublishIcon />}
                    onClick={handlePickImportFile}
                  >
                    {i18n.t("contactListItems.buttons.import")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenContactListItemModal}
                  >
                    {i18n.t("contactListItems.buttons.add")}
                  </Button>
                </>
              }
            />
            <input
              style={{ display: "none" }}
              id="upload"
              name="file"
              type="file"
              accept=".xls,.xlsx"
              onChange={() => {
                setConfirmOpen(true);
              }}
              ref={fileUploadRef}
            />
            <div className={classes.filters}>
              <TextField
                className={classes.search}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contactListItems.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{
                  "aria-label": i18n.t("contactListItems.searchPlaceholder"),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <Paper className={classes.mainPaper} variant="outlined">
              <div className={classes.tableScroll} onScroll={handleScroll}>
                <Table size="small" className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("contactListItems.table.name")}</TableCell>
                      <TableCell>
                        {i18n.t("contactListItems.table.number")}
                      </TableCell>
                      <TableCell>
                        {i18n.t("contactListItems.table.email")}
                      </TableCell>
                      <TableCell>
                        {i18n.t("contactListItems.table.whatsapp")}
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        {i18n.t("contactListItems.table.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {contacts.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell className={classes.nameCell}>
                            {contact.name}
                          </TableCell>
                          <TableCell>{contact.number}</TableCell>
                          <TableCell
                            className={contact.email ? undefined : classes.muted}
                          >
                            {contact.email || "—"}
                          </TableCell>
                          <TableCell>
                            {contact.isWhatsappValid ? (
                              <StatusBadge color="success" label="Válido" />
                            ) : (
                              <StatusBadge color="neutral" label="Inválido" />
                            )}
                          </TableCell>
                          <TableCell align="right" className={classes.actionsCell}>
                            <Tooltip title={i18n.t("contactListItems.dialog.edit")}>
                              <IconButton
                                size="small"
                                aria-label={`${i18n.t("contactListItems.dialog.edit")} ${contact.name}`}
                                onClick={() => hadleEditContact(contact.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Can
                              role={user.profile}
                              perform="contacts-page:deleteContact"
                              yes={() => (
                                <Tooltip
                                  title={i18n.t(
                                    "contactListItems.confirmationModal.deleteTitle"
                                  )}
                                >
                                  <IconButton
                                    size="small"
                                    className={classes.deleteButton}
                                    aria-label={`${i18n.t(
                                      "contactListItems.confirmationModal.deleteTitle"
                                    )} ${contact.name}`}
                                    onClick={() => {
                                      setConfirmOpen(true);
                                      setDeletingContact(contact);
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && contacts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} style={{ borderBottom: "none" }}>
                            <EmptyState
                              icon={<PeopleIcon />}
                              title="Nenhum contato encontrado nesta lista"
                              message={
                                searchParam
                                  ? "Nenhum contato corresponde à busca."
                                  : "Adicione um contato ou importe uma planilha (.xls ou .xlsx)."
                              }
                              action={
                                !searchParam && (
                                  <div className={classes.emptyActions}>
                                    <Button
                                      variant="outlined"
                                      startIcon={<PublishIcon />}
                                      onClick={handlePickImportFile}
                                    >
                                      {i18n.t("contactListItems.buttons.import")}
                                    </Button>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      startIcon={<AddIcon />}
                                      onClick={handleOpenContactListItemModal}
                                    >
                                      {i18n.t("contactListItems.buttons.add")}
                                    </Button>
                                  </div>
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {loading && <TableRowSkeleton columns={5} />}
                    </>
                  </TableBody>
                </Table>
              </div>
            </Paper>
          </>}
    </MainContainer>
  );
};

export default ContactListItems;
